import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Checkbox, Collapse, Flex, Input, Tag, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api-client';
import { confirmModelGroup, fetchModelSuggestions, type SuggestedGroup } from './api';

const { Text } = Typography;

const PAGE = 15;

const label = (brand: string, model: string, variant: string | null): string =>
  [brand, model, variant].filter((p) => p !== null && p.trim() !== '').join(' ');

/**
 * Codes that look like the same bike, matched in one press (spec 2026-10-26).
 *
 * The screen only proposes (Q85). Every code starts ticked and can be unticked,
 * the name can be edited, and the button says exactly what it will do.
 */
export function SuggestedGroups({ canManage }: { canManage: boolean }): ReactNode {
  const { t } = useTranslation();
  const suggestions = useQuery({ queryKey: ['model-map', 'suggestions'], queryFn: fetchModelSuggestions });
  const [shown, setShown] = useState(PAGE);

  const data = suggestions.data;
  if (data === undefined) return null;
  const groups = data.groups;
  const units = groups.reduce((n, g) => n + g.units, 0);

  return (
    <Flex vertical gap={12}>
      <Card size="small">
        <Flex vertical gap={4}>
          <Text strong>{t('vehicles.modelMap.suggest.title')}</Text>
          <Text type="secondary">{t('vehicles.modelMap.suggest.intro')}</Text>
          {groups.length > 0 && (
            <Text>{t('vehicles.modelMap.suggest.coverage', { groups: groups.length, units })}</Text>
          )}
        </Flex>
      </Card>

      {groups.length === 0 && <Text type="secondary">{t('vehicles.modelMap.suggest.empty')}</Text>}

      {groups.slice(0, shown).map((g) => (
        // Keyed on the codes and the name too: when a code is matched elsewhere the
        // card starts fresh, rather than still sending a code it no longer shows.
        <GroupCard
          key={`${g.key}|${g.model}|${g.codes.map((c) => c.itemCode).join(',')}`}
          group={g}
          canManage={canManage}
        />
      ))}

      {groups.length > shown && (
        <Button size="large" block onClick={() => { setShown((n) => n + PAGE); }}>
          {t('vehicles.modelMap.suggest.showMore', { count: Math.min(PAGE, groups.length - shown) })}
        </Button>
      )}

      {data.notMotorcycles.length > 0 && (
        <Collapse
          size="small"
          items={[
            {
              key: 'not-bikes',
              label: t('vehicles.modelMap.suggest.notBikes', { count: data.notMotorcycles.length }),
              children: (
                <Flex vertical gap={6}>
                  <Text type="secondary">{t('vehicles.modelMap.suggest.notBikesHelp')}</Text>
                  {data.notMotorcycles.map((c) => (
                    <Text key={c.itemCode}>
                      <span className="tabular">{c.itemCode}</span> · {c.description ?? '—'} · {c.units}
                    </Text>
                  ))}
                </Flex>
              ),
            },
          ]}
        />
      )}
    </Flex>
  );
}

function GroupCard({ group, canManage }: { group: SuggestedGroup; canManage: boolean }): ReactNode {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [ticked, setTicked] = useState<string[]>(group.codes.map((c) => c.itemCode));
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState({ brand: group.brand, model: group.model, variant: group.variant ?? '' });
  const [failure, setFailure] = useState<string | null>(null);

  // An edited name no longer points at the catalogue model the server found.
  const nameChanged =
    name.brand !== group.brand || name.model !== group.model || name.variant !== (group.variant ?? '');
  const existing = nameChanged ? null : group.existingModel;
  const modelLabel = existing?.label ?? label(name.brand, name.model, name.variant);
  const tickedUnits = group.codes.filter((c) => ticked.includes(c.itemCode)).reduce((n, c) => n + c.units, 0);

  const confirm = useMutation({
    mutationFn: () =>
      confirmModelGroup({
        itemCodes: ticked,
        ...(existing !== null
          ? { modelUid: existing.uid }
          : {
              brandName: name.brand.trim(),
              model: name.model.trim(),
              ...(name.variant.trim() !== '' ? { variant: name.variant.trim() } : {}),
            }),
      }),
    onSuccess: (result) => {
      message.success(
        t('vehicles.modelMap.suggest.done', { count: result.unitsCreated, model: result.model, codes: result.codes }),
      );
      void queryClient.invalidateQueries({ queryKey: ['model-map'] });
      void queryClient.invalidateQueries({ queryKey: ['units'] });
    },
    onError: (e: Error) => {
      // The server names any code that was matched meanwhile.
      setFailure(e instanceof ApiError ? e.message : t('vehicles.modelMap.failed'));
      // A code matched meanwhile drops out of the group on reload.
      void queryClient.invalidateQueries({ queryKey: ['model-map'] });
    },
  });

  const nameMissing = name.brand.trim() === '' || name.model.trim() === '';

  return (
    <Card size="small">
      <Flex vertical gap={8}>
        <Flex justify="space-between" align="center" gap={8} wrap>
          <Text strong>{modelLabel}</Text>
          <Flex gap={4} align="center">
            <Tag>{t('vehicles.modelMap.unitsShort', { count: group.units })}</Tag>
            <Tag color={existing !== null ? 'success' : undefined}>
              {existing !== null ? t('vehicles.modelMap.suggest.inCatalogue') : t('vehicles.modelMap.suggest.newModel')}
            </Tag>
          </Flex>
        </Flex>

        {editing && (
          <Flex vertical gap={6}>
            <label>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('vehicles.modelMap.brand')}
              </Text>
              <Input
                size="large"
                value={name.brand}
                onChange={(e) => { setName((n) => ({ ...n, brand: e.target.value })); }}
              />
            </label>
            <label>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('vehicles.modelMap.modelName')}
              </Text>
              <Input
                size="large"
                value={name.model}
                onChange={(e) => { setName((n) => ({ ...n, model: e.target.value })); }}
              />
            </label>
            <label>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('vehicles.modelMap.variant')}
              </Text>
              <Input
                size="large"
                value={name.variant}
                onChange={(e) => { setName((n) => ({ ...n, variant: e.target.value })); }}
              />
            </label>
          </Flex>
        )}

        <Checkbox.Group
          value={ticked}
          onChange={(v) => { setTicked(v); }}
          style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
        >
          {group.codes.map((c) => (
            <Checkbox key={c.itemCode} value={c.itemCode} style={{ minHeight: 32 }}>
              <span className="tabular">{c.itemCode}</span>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {' '}
                · {c.units} · {c.description ?? t('vehicles.modelMap.suggest.noDescription')}
              </Text>
            </Checkbox>
          ))}
        </Checkbox.Group>

        {canManage && (
          <>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {existing !== null
                ? t('vehicles.modelMap.suggest.consequenceExisting', { model: modelLabel })
                : t('vehicles.modelMap.suggest.consequence', { model: modelLabel })}
            </Text>
            {failure !== null && <Alert type="error" showIcon message={failure} />}
            <Flex gap={8} wrap>
              <Button
                type="primary"
                size="large"
                disabled={ticked.length === 0 || nameMissing}
                loading={confirm.isPending}
                onClick={() => {
                  setFailure(null);
                  confirm.mutate();
                }}
              >
                {ticked.length === 1
                  ? t('vehicles.modelMap.suggest.confirmOne', { units: tickedUnits })
                  : t('vehicles.modelMap.suggest.confirm', { codes: ticked.length, units: tickedUnits })}
              </Button>
              <Button size="large" onClick={() => { setEditing((e) => !e); }}>
                {editing ? t('vehicles.modelMap.suggest.doneEditing') : t('vehicles.modelMap.suggest.edit')}
              </Button>
            </Flex>
          </>
        )}
      </Flex>
    </Card>
  );
}
